import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './Header.css';

const Header = () => {
    const [activeTab, setActiveTab] = useState('');
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
    const scrollPosition = React.useRef(0);
    const navigate = useNavigate();
    const location = useLocation();

    // 根据当前路径设置活动标签
    useEffect(() => {
        const path = location.pathname;
        if (path === '/about') {
            setActiveTab('about');
        } else if (path === '/event') {
            setActiveTab('event');
        } else if (path === '/business') {
            setActiveTab('business');
        } else {
            setActiveTab('');
        }
    }, [location.pathname]);

    // 控制移动端菜单和下载模态窗打开时的背景滚动和模糊效果
    useEffect(() => {
        if (isMobileMenuOpen || isDownloadModalOpen) {
            // 保存当前滚动位置
            scrollPosition.current = window.scrollY;
            // 阻止背景滚动
            document.body.style.overflow = 'hidden';
            document.body.style.position = 'fixed';
            document.body.style.top = `-${scrollPosition.current}px`;
            document.body.style.left = '0';
            document.body.style.right = '0';
            document.body.style.width = '100%';
            // 添加模糊效果类
            document.body.classList.add('menu-open-blur');
        } else {
            // 恢复背景滚动
            document.body.style.overflow = '';
            document.body.style.position = '';
            document.body.style.top = '';
            document.body.style.left = '';
            document.body.style.right = '';
            document.body.style.width = '';
            // 移除模糊效果类
            document.body.classList.remove('menu-open-blur');
            // 恢复到之前保存的滚动位置
            window.scrollTo(0, scrollPosition.current);
        }
    }, [isMobileMenuOpen, isDownloadModalOpen]);

    const handleTabClick = (tabName) => {
        setActiveTab(tabName);
        if (isMobileMenuOpen) {
            setIsMobileMenuOpen(false); // 选择后关闭菜单
        }
        
        // 导航到对应页面
        switch (tabName) {
            case 'about':
                navigate('/about');
                break;
            case 'event':
                navigate('/event');
                break;
            case 'business':
                navigate('/business');
                break;
            default:
                break;
        }
    };

    const toggleMobileMenu = () => {
        setIsMobileMenuOpen(!isMobileMenuOpen);
    };

    const handleLogoClick = () => {
        navigate('/home');
        if (isMobileMenuOpen) {
            setIsMobileMenuOpen(false); // 如果移动端菜单打开，则关闭
        }
    };

    const handleDownloadClick = () => {
        setIsDownloadModalOpen(true);
        if (isMobileMenuOpen) {
            setIsMobileMenuOpen(false); // 如果移动端菜单打开，则关闭
        }
    };

    const handleDownloadModalClose = () => {
        setIsDownloadModalOpen(false);
    };

    // 监听全局事件来打开下载弹窗
    useEffect(() => {
        const handleOpenDownloadModal = () => {
            setIsDownloadModalOpen(true);
        };

        window.addEventListener('openDownloadModal', handleOpenDownloadModal);

        return () => {
            window.removeEventListener('openDownloadModal', handleOpenDownloadModal);
        };
    }, []);

    // 监听窗口大小变化，当从移动端切换到桌面端时自动关闭菜单
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth > 1024 && isMobileMenuOpen) {
                setIsMobileMenuOpen(false);
            }
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [isMobileMenuOpen]);

    return (
        <header className="header">
            <div className="header-content">
                {/* 左侧 Logo */}
                <div className="header-left">
                    <div className="logo-container" onClick={handleLogoClick}>
                        <img 
                            src={require('../images/home/logo_g.png')} 
                            alt="Logo" 
                            className="header-logo default-logo"
                        />
                        <img 
                            src={require('../images/home/logo_b.png')} 
                            alt="Logo" 
                            className="header-logo hover-logo"
                        />
                    </div>
                </div>

                {/* 中间导航 */}
                <div className="header-center">
                    <nav className="header-nav">
                        <div 
                            className={`nav-item ${activeTab === 'about' ? 'active' : ''}`}
                            onClick={() => handleTabClick('about')}
                        >
                            About
                            {activeTab === 'about' && <div className="nav-indicator"></div>}
                        </div>
                        <div 
                            className={`nav-item ${activeTab === 'event' ? 'active' : ''}`}
                            onClick={() => handleTabClick('event')}
                        >
                            Event
                            {activeTab === 'event' && <div className="nav-indicator"></div>}
                        </div>
                        <div 
                            className={`nav-item ${activeTab === 'business' ? 'active' : ''}`}
                            onClick={() => handleTabClick('business')}
                        >
                            For Business
                            {activeTab === 'business' && <div className="nav-indicator"></div>}
                        </div>
                    </nav>
                </div>

                {/* 桌面端右侧 - 下载按钮 */}
                <div className="header-right desktop-only">
                    <button className="download-button" onClick={handleDownloadClick}>Download</button>
                </div>

                {/* 移动端右侧 - 菜单按钮 */}
                <div className="header-right mobile-only">
                    <button className="mobile-menu-button" onClick={toggleMobileMenu}>
                        <img 
                            src={require(isMobileMenuOpen ? '../images/home/close.png' : '../images/home/list_g.png')} 
                            alt="Menu" 
                            className="menu-icon default-menu-icon"
                        />
                        {!isMobileMenuOpen && (
                            <img 
                                src={require('../images/home/list_b.png')} 
                                alt="Menu" 
                                className="menu-icon hover-menu-icon"
                            />
                        )}
                    </button>
                </div>
            </div>
            
            {/* 移动端全屏菜单 */}
            {isMobileMenuOpen && (
                <div className="mobile-menu-overlay">
                    <div className="mobile-menu-content">
                        <div 
                            className={`mobile-menu-item ${activeTab === 'about' ? 'active' : ''}`}
                            onClick={() => handleTabClick('about')}
                        >
                            <span className="mobile-menu-text">
                                About
                                {activeTab === 'about' && <div className="mobile-nav-indicator"></div>}
                            </span>
                        </div>
                        <div 
                            className={`mobile-menu-item ${activeTab === 'event' ? 'active' : ''}`}
                            onClick={() => handleTabClick('event')}
                        >
                            <span className="mobile-menu-text">
                                Event
                                {activeTab === 'event' && <div className="mobile-nav-indicator"></div>}
                            </span>
                        </div>
                        <div 
                            className={`mobile-menu-item ${activeTab === 'business' ? 'active' : ''}`}
                            onClick={() => handleTabClick('business')}
                        >
                            <span className="mobile-menu-text">
                                For Business
                                {activeTab === 'business' && <div className="mobile-nav-indicator"></div>}
                            </span>
                        </div>
                        <div 
                            className="mobile-menu-item download-item"
                            onClick={handleDownloadClick}
                        >
                            Download
                        </div>
                    </div>
                </div>
            )}

            {/* 下载模态窗 */}
            {isDownloadModalOpen && (
                <div className="download-modal-overlay" onClick={handleDownloadModalClose}>
                    <div className="download-modal-content" onClick={(e) => e.stopPropagation()}>
                        <button className="download-modal-close" onClick={handleDownloadModalClose}>
                            <img 
                                src={require('../images/download/close.png')} 
                                alt="Close" 
                            />
                        </button>
                        <div className="download-modal-body">
                            <img 
                                src={require('../images/download/qr.png')} 
                                alt="QR Code" 
                                className="qr-code"
                            />
                            <div className="download-or-text">OR</div>
                            <img 
                                src={require('../images/download/store.png')} 
                                alt="App Store" 
                                className="store-badge"
                                onClick={() => window.open('https://apps.apple.com/us/app/marlo-connect-meet-nearby/id6450737280?l=zh-Hans-CN', '_blank')}
                                style={{ cursor: 'pointer' }}
                            />
                                                        <p className="download-description">
                                Download Marlo to find your<br/>people nearby.
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </header>
    );
};

export default Header;